import type { TagType } from "@/components/Tag/Tag";
import { PayoutProfile } from "./queries";
import { MetricCardVariant } from "@/components/MetricCard/MetricCard.types";

export interface StatusData {
  label: string;
  tagType: TagType;
  variant?: MetricCardVariant;
}

const STATUS_DATA_MAP: Record<string, StatusData> = {
  active: {
    label: "Active",
    tagType: "success",
    variant: "success"
  },
  draft: {
    label: "Incomplete",
    tagType: "information",
    variant: "info"
  },
  processing: {
    label: "Processing",
    tagType: "warn",
    variant: "warn"
  },
  waiting_for_approval: {
    label: "Waiting for Approval",
    tagType: "warn",
    variant: "warn"
  },
  waiting_for_bank_info: {
    label: "Waiting for Bank Info",
    tagType: "warn",
    variant: "warn"
  },
  error: {
    label: "Profile Error",
    tagType: "error",
    variant: "error"
  },
  deactivated: {
    label: "Inactive",
    tagType: "surface"
  }
};

export const getStatusData = ({ status, workdayServerException, workdayUserError }: PayoutProfile): StatusData => {
  const hasWorkdayError = !!workdayUserError || !!workdayServerException;
  const isError = status === "failed_to_sync" || hasWorkdayError;
  const normalizedStatus = isError ? "error" : status;

  return (
    STATUS_DATA_MAP[normalizedStatus] || {
      label: status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " "),
      tagType: "surface",
      variant: "info"
    }
  );
};

export function isW8(profile: PayoutProfile) {
  return profile.taxFormType === "w8";
}

export function isPersonal(profile: PayoutProfile) {
  return profile.taxClassification === "individual_sole_proprietor" || isW8(profile);
}

export function taxClassificationDisplay(classification: string) {
  const values: Record<string, string> = {
    individual_sole_proprietor: "Individual / Sole Proprietor",
    c_corporation: "C Corporation",
    s_corporation: "S Corporation",
    partnership: "Partnership",
    trust_estate: "Trust/Estate",
    llc: "Limited Liability Company (LLC)"
  };
  return values[classification] || classification;
}

/** TanStack desc multiplies sortingFn by -1; compensate so nulls stay last either way. */
export function compareIsoDatesNullsLast(
  a: string | null | undefined,
  b: string | null | undefined,
  desc: boolean
): number {
  if (a == null && b == null) return 0;
  if (a == null) return desc ? -1 : 1;
  if (b == null) return desc ? 1 : -1;
  return a.localeCompare(b);
}

/** Maps `Finance::PayoutProfile` `llc_classification` enum values for display. */
export function llcClassificationDisplay(classification: string) {
  const values: Record<string, string> = {
    c_corp: "C Corporation",
    s_corp: "S Corporation",
    partnership: "Partnership"
  };
  return values[classification] || classification;
}
